import { TypographyP2 } from '@heidi/design-system/typography';
import { useSetAtom } from 'jotai';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { setOpenArtifactAtom } from '../../data/projects/artifacts/state';

import { LabelledContextPanel } from './labelled-context-panel';
import { MailAttachmentFootnote } from './mail-attachment-footnote';
import {
  MailComposeApprovalLayout,
  type MailComposeValues,
} from './mail-compose-approval-layout';
import {
  useNangoApprovalSubmit,
  type NangoConnectorApprovalCardProps,
} from './nango-approval-submit';
import {
  buildMailModifiedInput,
  countRequestedMailAttachments,
  formatAddressList,
  parseAddressListInput,
  readMailApprovalFields,
  readMailReplyPreview,
  readMailSender,
  resolveMailAttachmentVerification,
  type MailApprovalFields,
  type MailReplyApprovalPreview,
} from './nango-connector-approval';

export function MailApprovalCard({
  approval,
  onResolve,
}: NangoConnectorApprovalCardProps) {
  const intl = useIntl();
  const { pendingVerdict, isBusy, submit } = useNangoApprovalSubmit(
    approval.approval_id,
    onResolve
  );
  const initial = useMemo(
    () => readMailApprovalFields(approval.tool_name, approval.tool_input),
    [approval.tool_input, approval.tool_name]
  );
  const replyPreview = useMemo(
    () => readMailReplyPreview(approval.context),
    [approval.context]
  );
  const sender = useMemo(
    () => readMailSender(approval.pendingContext, approval.context),
    [approval.context, approval.pendingContext]
  );
  const requestedAttachments = useMemo(
    () => countRequestedMailAttachments(approval.tool_name, approval.tool_input),
    [approval.tool_input, approval.tool_name]
  );
  const attachmentCheck = useMemo(
    () =>
      resolveMailAttachmentVerification(requestedAttachments, [
        approval.context,
        approval.pendingContext,
      ]),
    [approval.context, approval.pendingContext, requestedAttachments]
  );
  const attachmentPreview = attachmentCheck.preview;
  const attachmentsVerified =
    attachmentCheck.status === 'idle' || attachmentCheck.status === 'verified';
  const setOpenArtifact = useSetAtom(setOpenArtifactAtom);

  const [values, setValues] = useState<MailComposeValues>(() => {
    const compose = initial?.kind === 'compose' ? initial : null;
    return {
      to: formatAddressList(compose?.to ?? []),
      cc: formatAddressList(compose?.cc ?? []),
      bcc: formatAddressList(compose?.bcc ?? []),
      subject: compose?.subject ?? '',
      body: initial?.body ?? '',
    };
  });

  if (!initial) return null;

  const isReply = initial.kind === 'reply';
  const isDraft =
    initial.kind === 'compose' && initial.toolName === 'integrations.mail.create_draft';
  const fieldsReady =
    isReply || isDraft
      ? true
      : parseAddressListInput(values.to).length > 0 &&
        values.subject.trim().length > 0 &&
        values.body.trim().length > 0;
  const canApprove = fieldsReady && attachmentsVerified && sender !== null;
  const fromLine = sender
    ? sender.address
      ? `${sender.label} · ${sender.address}`
      : sender.label
    : intl.formatMessage({
        defaultMessage: "Heidi couldn't confirm which account this sends from.",
        id: '5PDYcIWqBt',
      });

  const currentFields = (): MailApprovalFields =>
    isReply
      ? {
          kind: 'reply',
          toolName: 'integrations.mail.reply',
          messageId: initial.messageId,
          body: values.body,
        }
      : {
          kind: 'compose',
          toolName: initial.toolName,
          to: parseAddressListInput(values.to),
          cc: parseAddressListInput(values.cc),
          bcc: parseAddressListInput(values.bcc),
          subject: values.subject,
          body: values.body,
        };

  const title =
    initial.toolName === 'integrations.mail.create_draft'
      ? intl.formatMessage({
          defaultMessage: 'Save email draft',
          id: 'AQlXn5r7L9',
        })
      : isReply
        ? intl.formatMessage({ defaultMessage: 'Send email reply', id: '0vIVBoAl3q' })
        : intl.formatMessage({ defaultMessage: 'Send email', id: 'sZIoMyo0mu' });

  const approveLabel =
    initial.toolName === 'integrations.mail.create_draft'
      ? intl.formatMessage({ defaultMessage: 'Save draft', id: 'R2zJFpVjmW' })
      : intl.formatMessage({ defaultMessage: 'Send', id: '9WRlF4R2gm' });

  return (
    <MailComposeApprovalLayout
      testId={`nango-mail-approval-${approval.approval_id}`}
      fieldTestIdPrefix="nango-mail-approval"
      title={title}
      values={values}
      onChange={(field, value) => setValues((prev) => ({ ...prev, [field]: value }))}
      fromLine={fromLine}
      headerOverride={
        isReply ? (
          <div className="border-b border-border px-3 py-2">
            <LabelledContextPanel
              label={intl.formatMessage({
                defaultMessage: 'Reply to',
                id: 'P6SLRPKzbr',
              })}
            >
              {replyPreview ? (
                <MailReplyRecipient preview={replyPreview} />
              ) : (
                <TypographyP2 className="break-all font-mono text-sm text-text-secondary">
                  {initial.messageId}
                </TypographyP2>
              )}
            </LabelledContextPanel>
          </div>
        ) : undefined
      }
      footnote={
        requestedAttachments > 0 ? (
          <MailAttachmentFootnote
            attachments={attachmentPreview}
            status={attachmentCheck.status}
            isBusy={isBusy}
            onOpen={setOpenArtifact}
          />
        ) : undefined
      }
      isBusy={isBusy}
      canApprove={canApprove}
      approveLabel={approveLabel}
      pendingVerdict={pendingVerdict}
      onApprove={() =>
        submit(
          'approve',
          buildMailModifiedInput(approval.tool_input, currentFields(), sender)
        )
      }
      onDeny={() => submit('deny')}
    />
  );
}

function MailReplyRecipient({ preview }: { preview: MailReplyApprovalPreview }) {
  return (
    <div
      className="flex min-w-0 flex-col gap-0.5"
      data-testid="nango-mail-approval-reply-to"
    >
      <TypographyP2 className="truncate text-sm text-text-primary">
        {preview.to.join(', ')}
      </TypographyP2>
      {preview.subject ? (
        <TypographyP2 className="truncate text-sm text-text-secondary">
          {preview.subject}
        </TypographyP2>
      ) : null}
    </div>
  );
}
